import React, { useEffect, useRef } from 'react';
import { FC, useState } from 'react';
import { useRouter } from 'next/router';
import { Modal, Button, Notification, LoadingIndicator } from '@lululemon/b2b-ecom-pattern-library';
import { useFormat } from 'helpers/hooks/useFormat';
import FileUploader from './file-uploader';
import styles from './sku-upload-modal.module.scss';
import { AggregatedSkuDetail, SkuDetail, useProduct } from 'helpers/hooks/useProduct';
import { useCart } from 'context/cart';
import { AddCartItemsResponse, CartSkuItem } from 'types/cart';
import { DELETE_CART_ERROR } from 'helpers/constants';
import { useAccount, useBusinessUnit } from 'frontastic/hooks';
import { getBusinessUnitCountry, isNorthAmericaCountry } from 'helpers/utils/countryHelper';
import { isCanadianUser } from 'helpers/yogaMatRestrictions';
import { YOGA_MAT_CONSTANTS } from 'shared/utils/constants';
import { FEATURE_FLAG_LIST, useFeatureFlags } from 'hooks';

export const TEST_IDS = {
  container: 'sku-upload-modal-container_test-id',
  wrapper: 'sku-upload-modal-wrapper_test-id',
  message: 'sku-upload-modal-message_test-id',
};

export const COPY = {
  title: 'Import all Products with a CSV',
  message: 'Use a CSV file to import all your product SKUs and quantities at once.',
  cta: 'Add Products to Bag',
  invalidSkuNotice: 'The file contains invalid SKUs in the following rows :',
  addToCartLoadingModalTitle: 'Upload in progress',
  addToCartLoadingModalDescription:
    'We are importing all the products from your CSV file into your bag. This may take a moment. Do not refresh or sign out, it would cancel the upload.',
  missingSkuErrorMessage:
    'contains invalid SKUs in the following rows. Please remove invalid SKUs and try uploading the file again.',
  validateSkuErrorMessage: 'An error occurred while validating skus and adding to cart your request. Please try again.',
  yogaMatMixErrorMessage:
    'contains both yoga mats and other products.\n\nFor Canadian partners, yoga mats ship from a dedicated fulfillment center and must be ordered separately from other items.\n\nPlease split this file into two different orders.',
};

type SkuUploadModalProps = {
  visible: boolean;
  setIsOpen: (value: boolean) => void;
};

type DeleteCartResponseType = { isError: boolean; data: object | null };

const ADD_TO_CART_BATCH_SIZE = 20; // Define the batch size for adding items to cart

export type ErrorObject = {
  successStatus: boolean;
  errorStatus: boolean;
  warningStatus: boolean;
  responseMessage: string;
  invalidSkuDetailsHeading?: string;
  invalidSkuDetails: string[];
};
const SkuUploadModal: FC<SkuUploadModalProps> = ({ visible, setIsOpen }) => {
  const modalRef = useRef<HTMLDivElement | null>(null);

  const { fetchSkusInfo } = useProduct();
  const { addCartItems, cart, deleteCart, getLatestCart, isRemoteAddToCartLocked } = useCart();
  const { account } = useAccount();
  const { businessUnit } = useBusinessUnit();
  const { isFeatureEnabled } = useFeatureFlags();
  const isYogaMatEnabled = isFeatureEnabled(FEATURE_FLAG_LIST.YOGA_MAT_ENABLEMENT);
  const isYogaMatWithOtherOrderFixEnabled = isFeatureEnabled(FEATURE_FLAG_LIST.YOGA_MAT_WITH_OTHER_ORDER_FIX);
  // Header-based CSV upload is a North America only experience; INTL business units keep the legacy format.
  const isPartnerMetadataUploadEnabled =
    isFeatureEnabled(FEATURE_FLAG_LIST.SKU_UPLOAD_PARTNER_METADATA) &&
    isNorthAmericaCountry(getBusinessUnitCountry(businessUnit));
  const router = useRouter();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errorObj, setErrorObj] = useState<ErrorObject>({
    successStatus: false,
    errorStatus: false,
    warningStatus: false,
    responseMessage: '',
    invalidSkuDetailsHeading: '',
    invalidSkuDetails: [],
  });
  const [skuDetails, setSkuDetails] = useState<SkuDetail[]>([]);
  const [missingSkus, setMissingSkus] = useState<AggregatedSkuDetail[]>([]);
  const [isSkuValidating, setIsSkuValidating] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const { formatMessage } = useFormat({ name: 'common' });

  // This useEffect is added to override requestOnClose prop for the modal
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      const closeButton = target.closest('button');
      if (closeButton && Array.from(closeButton.classList).some((cls) => cls.startsWith('modal_closeButton'))) {
        //Close only when clicked on close button
        setIsOpen(false);
      }
    };

    const modalEl = modalRef.current;
    modalEl?.addEventListener('click', handleClick);

    return () => {
      modalEl?.removeEventListener('click', handleClick);
    };
  }, []);

  useEffect(() => {
    const shouldLockScroll = visible || isAddingToCart;
    if (!shouldLockScroll) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [visible, isAddingToCart]);

  const processCartItems = async (validSkusWithQuantity: CartSkuItem[]): Promise<AddCartItemsResponse> => {
    // Split validSkusWithQuantity into batches of 20
    const batches = splitIntoBatches(validSkusWithQuantity, ADD_TO_CART_BATCH_SIZE);

    let lastResponse: any = null;

    // Process each batch sequentially
    for (const batch of batches) {
      lastResponse = await addCartItems(batch); // Capture the response from the last call
    }

    return lastResponse; // Return the response from the last batch
  };

  const toCartSkuItem = (sku: AggregatedSkuDetail): CartSkuItem => ({
    skuId: sku.skuId,
    quantity: sku.quantity,
    upi: sku.upi,
    upc: sku.upc,
    partnerItemId: sku.partnerItemId,
  });

  // Helper function to split an array into batches
  function splitIntoBatches(array: any[], batchSize: number): any[][] {
    const batches: any[][] = [];
    for (let i = 0; i < array.length; i += batchSize) {
      batches.push(array.slice(i, i + batchSize));
    }
    return batches;
  }

  const handleAddToCart = async () => {
    if (skuDetails.length === 0 || isRemoteAddToCartLocked) {
      return;
    }
    try {
      setIsSkuValidating(true);
      const skuData: { missingSkus: AggregatedSkuDetail[]; validSkus: AggregatedSkuDetail[] } = await fetchSkusInfo(
        skuDetails,
      );

      const isSkuDataValid = skuData.missingSkus.length === 0 && skuData.validSkus.length > 0;

      // Handle the case when bff returns 500 or empty response
      if (skuData.missingSkus.length === 0 && skuData.validSkus.length === 0) {
        setIsSkuValidating(false);
        setErrorObj({
          successStatus: false,
          errorStatus: true,
          warningStatus: false,
          responseMessage: formatMessage({
            id: 'validateSkuErrorMessage',
            defaultMessage: COPY.validateSkuErrorMessage,
          }),
          invalidSkuDetailsHeading: '',
          invalidSkuDetails: [],
        });
        return;
      }

      setMissingSkus(skuData.missingSkus);

      // For Canadian users: block if the file contains a mix of yoga mats and non-yoga mats
      const isCanadian = isCanadianUser(account, businessUnit);
      if (isYogaMatEnabled && isCanadian && skuData.validSkus.length > 0) {
        const isYogaMat = (sku: { categoryBreadcrumb?: string | null }) =>
          !!(
            sku.categoryBreadcrumb &&
            sku.categoryBreadcrumb.toLowerCase().includes(YOGA_MAT_CONSTANTS.CATEGORY_IDENTIFIER)
          );
        const hasYogaMats = skuData.validSkus.some(isYogaMat);
        const hasNonYogaMats = skuData.validSkus.some((sku) => !isYogaMat(sku));
        if (hasYogaMats && hasNonYogaMats) {
          setIsSkuValidating(false);
          setErrorObj({
            successStatus: false,
            errorStatus: true,
            warningStatus: false,
            responseMessage: `${selectedFile?.name} ${formatMessage({
              id: 'yogaMatMixErrorMessage',
              defaultMessage: COPY.yogaMatMixErrorMessage,
            })}`,
            invalidSkuDetailsHeading: '',
            invalidSkuDetails: [],
          });
          return;
        }
      }

      // Re-fetch instead of trusting the possibly-stale local cart, so a cart created by another
      // tab/browser since this modal loaded is still cleared before the CSV batch is added.
      const latestCart = isYogaMatWithOtherOrderFixEnabled ? await getLatestCart() : cart;
      if (latestCart?.cartId && isSkuDataValid) {
        const deleteCartResponse: DeleteCartResponseType = await deleteCart();
        if (deleteCartResponse?.isError) {
          throw new Error(DELETE_CART_ERROR);
        }
      }

      // Start adding to cart process
      // Only proceed if there are no missing SKUs
      if (isSkuDataValid) {
        setIsAddingToCart(true);
        const cartPayload = isPartnerMetadataUploadEnabled
          ? skuData.validSkus.map(toCartSkuItem)
          : (skuData.validSkus as unknown as CartSkuItem[]);

        const addToCartFinalResponse = await processCartItems(cartPayload);
        if (addToCartFinalResponse) {
          setIsOpen(false);
          setIsAddingToCart(false);
          router.push('/cart');
        }
      }
      setIsSkuValidating(false);
    } catch (error) {
      console.log('Error during SKU validation or adding to cart:', error);
      setIsSkuValidating(false);
      setIsAddingToCart(false);
      setErrorObj({
        successStatus: false,
        errorStatus: true,
        warningStatus: false,
        responseMessage: formatMessage({ id: 'validateSkuErrorMessage', defaultMessage: COPY.validateSkuErrorMessage }),
        invalidSkuDetailsHeading: '',
        invalidSkuDetails: [],
      });
    }
  };

  return (
    <>
      <div data-testid={TEST_IDS.container}>
        {isAddingToCart ? (
          <Modal
            onRequestClose={() => null}
            visible={isAddingToCart}
            className={`modal_modal__t6ZLL ${styles.modalLoading}`}
          >
            {() => (
              <div className={'mx-18 mb-0 mt-30'}>
                <h1 className={styles.heading}>
                  {' '}
                  {formatMessage({
                    id: 'addToCartLoadingModal.title',
                    defaultMessage: COPY.addToCartLoadingModalTitle,
                  })}
                </h1>
                <div className={'mt-12'}>
                  <span>
                    {formatMessage({
                      id: 'addToCartLoadingModal.description',
                      defaultMessage: COPY.addToCartLoadingModalDescription,
                    })}
                  </span>
                </div>
                <div className={'mt-30 flex items-center justify-center'}>
                  <LoadingIndicator />
                </div>
              </div>
            )}
          </Modal>
        ) : (
          <Modal
            ref={modalRef}
            visible={visible}
            closeMessage={close}
            data-testid={TEST_IDS.wrapper}
            className={`modal_modal__t6ZLL ${styles.modal}`}
          >
            <div>
              <h1 className={styles.heading} id="dialog-modal-checkout-error">
                {formatMessage({ id: 'title', defaultMessage: COPY.title })}
              </h1>
              <div className={styles.modalInnerContainer}>
                {/* File validation error notification */}
                {errorObj.errorStatus && (
                  <div className={styles.yogaMatMixErrorMessage}>
                    <Notification className={styles.notificationBanner} type="error" variant="banner" visible>
                      {formatMessage({
                        id: 'unable.to.load',
                        defaultMessage: errorObj.responseMessage,
                      })}
                    </Notification>
                  </div>
                )}
                {/* Sku validation error notification */}
                {missingSkus.length > 0 && (
                  <Notification className={styles.notificationBanner} type="error" variant="banner" visible>
                    {`${selectedFile?.name} ${formatMessage({
                      id: 'missingSkuErrorMessage',
                      defaultMessage: COPY.missingSkuErrorMessage,
                    })}
`}
                  </Notification>
                )}
                {errorObj?.invalidSkuDetails?.length > 0 && (
                  <div className={`mt-15 ${styles.invalidSkusContainer}`}>
                    <p>{errorObj.invalidSkuDetailsHeading || COPY.invalidSkuNotice}</p>
                    <ul>
                      {errorObj.invalidSkuDetails.map((item: any, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {missingSkus?.length > 0 && (
                  <div className={`mt-15 ${styles.invalidSkusContainer}`}>
                    <p>
                      {formatMessage({
                        id: 'invalidSkuNotice',
                        defaultMessage: COPY.invalidSkuNotice,
                      })}
                    </p>
                    <ul>
                      {missingSkus.map((item: any, index: number) => (
                        <li key={index}>
                          {formatMessage({
                            id: 'invalidSkuListItem',
                            defaultMessage: `Row ${item.row}, SKU ${item.skuId}`,
                            values: { skuId: item.skuId, row: item.row },
                          })}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className={styles.message} data-testid={TEST_IDS.message}>
                  {formatMessage({
                    id: 'message',
                    defaultMessage: COPY.message,
                  })}
                </p>
                <FileUploader
                  selectedFile={selectedFile}
                  setSelectedFile={setSelectedFile}
                  setNotifcationObj={setErrorObj}
                  setSkuDetails={setSkuDetails}
                  setMissingSkus={setMissingSkus}
                  isEnhancedCsvUploadEnabled={!!isPartnerMetadataUploadEnabled}
                />
                <div className="mt-32 flex justify-end">
                  <Button
                    className={styles.ctaButton}
                    variant="filledImpact"
                    onClick={handleAddToCart}
                    disabled={isRemoteAddToCartLocked}
                  >
                    {!isSkuValidating ? (
                      formatMessage({
                        id: 'cta',
                        defaultMessage: COPY.cta,
                      })
                    ) : (
                      <LoadingIndicator color="white" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </>
  );
};

export default SkuUploadModal;
